import { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { getCompanyLogo, getStaticStores, useStores } from '@/utils'
import { MyHead } from '@/components/molecules/MyHead'
import { ICompanyBase, IJobBase, Language } from '@/types'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import { PrimaryHeading } from '@/components/atoms/PrimaryHeading'
import { Button } from '@/components/atoms/Button'
import { JobItemList } from '@/components/organisms/JobItemList'
import { CompanyDetailHeader } from '@/components/organisms/CompanyDetailHeader'
import styles from './index.module.scss'

type Props = {
  company: ICompanyBase
  staticCompanyJobs: IJobBase[]
  staticHasNextPage: boolean
}

const NUM_OF_ITEMS = 10

const JobsOfCompany: NextPage<Props> = observer(({ company, staticCompanyJobs, staticHasNextPage }) => {
  const { t } = useTranslation(['p'])
  const { jobs, companies, viewer } = useStores()
  const companyInstance = companies.createCompanyInstance(company)
  const title = t('の転職・副業・インターン情報', { name: company.name })
  const description = t(
    'の転職・副業・インターン等の求人情報があります。AQA はスタートアップ・ベンチャー企業の求人サイトです。',
    { name: company.name }
  )

  // ISRで取得した配列より大きい場合、ページ送りの初回リクエストが実行されていることにする
  const hasDoneLoadMore = jobs.companyJobs[company.slug]?.length > staticCompanyJobs.length

  const [isFetching, setIsFetching] = useState(false)

  const fetchCompanyJobs = async () => {
    if (isFetching) {
      return
    }

    setIsFetching(true)
    await jobs.fetchCompanyJobs({
      companySlug: company.slug,
      limit: hasDoneLoadMore ? NUM_OF_ITEMS : NUM_OF_ITEMS * 2,
      shouldRefresh: !hasDoneLoadMore,
    })
    setIsFetching(false)
  }

  // ブラウザ側での初期化時に Store に hasNextPage を設定
  useEffect(() => {
    jobs.updateHasNextCompanyJobsPage(company.slug, staticHasNextPage)
  }, [])

  useEffect(() => {
    if (!company || !companyInstance) {
      return
    }

    if (viewer.isInitialized && viewer.isSignedIn) {
      companyInstance.fetch()
    }
  }, [viewer.isInitialized, company])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead title={title} description={description} image={getCompanyLogo(company, true)} />
      <ProfileSingleColumnContentTemplate header={<CompanyDetailHeader company={companyInstance} />} hasHeaderImage>
        <div className={styles.heading}>
          <PrimaryHeading backgroundText='JOBS'>{title}</PrimaryHeading>
        </div>
        <div className={styles.content}>
          <JobItemList items={hasDoneLoadMore ? jobs.companyJobs[company.slug] : staticCompanyJobs} />
        </div>
        {jobs.hasNextCompanyJobsPage[company.slug] && (
          <div className={styles.button}>
            <Button
              styleType='default'
              componentType='button'
              buttonSize='large'
              isLoading={isFetching}
              onClick={() => fetchCompanyJobs()}
            >
              {t('+ Load More')}
            </Button>
          </div>
        )}
      </ProfileSingleColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getServerSideProps: GetServerSideProps = async ({ locale, params }) => {
  const { companies, jobs, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const companySlug = params.id as string
  const [company, staticCompanyJobs] = await Promise.all([
    companies.fetchCompany({ slug: companySlug, limitOfJob: 0 }),
    jobs.fetchCompanyJobs({ companySlug, limit: NUM_OF_ITEMS, shouldRefresh: true }),
  ])

  if (!company) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      company,
      staticCompanyJobs,
      staticHasNextPage: jobs.hasNextCompanyJobsPage[companySlug],
      ...(await serverSideTranslations(locale, ['global', 'p', 'jobs', 'companies-users-[id]', 'companies-[id]'])),
    },
  }
}

export default JobsOfCompany
